import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import styled from 'styled-components';

import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { TerminatedIcon } from '@/assets/icons/investment-status/treminated';
import { ConfirmationStatus } from '@/components/authentication/statuses/confirmation';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { themeVar } from '@/styles/theme';
import { multiFactor, PhoneAuthProvider } from 'firebase/auth';
import { Helmet as Head } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import VerificationRequired from './modals/verificationRequired';
import { AddMobilePhoneNumber } from './steps/addMobilePhoneNumber';
import { VerifyMobilePhoneNumber } from './steps/verifyMobilePhoneNumber';

export enum TWO_FACTOR_AUTHENTICATION_STATUS_ENUM {
  DEFAULT = 'default',
  VERIFY = 'verify',
  CONFIRMATION = 'confirmation',
  EXPIRED = 'expired'
}

const TwoFactorAuthenticationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, user, signOut } = useAuth();
  const { default: defaultErrorMessage } = useErrorMessages();

  const twoFactorAuthenticationMobileNumber = location?.state?.twoFactorAuthenticationMobileNumber;

  const changeMobileNumber = location?.state?.changeMobileNumber;

  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [status, setStatus] = useState<TWO_FACTOR_AUTHENTICATION_STATUS_ENUM>(TWO_FACTOR_AUTHENTICATION_STATUS_ENUM.DEFAULT);
  const [mobileNumber, setMobileNumber] = useState<{ countryCode: string; number: string }>({ countryCode: '', number: '' });
  const [verificationId, setVerificationId] = useState<string>('');
  const [resendVerificationCodeLoading, setResendVerificationCodeLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!twoFactorAuthenticationMobileNumber) return;
    openModalWindow('verification-required', twoFactorAuthenticationMobileNumber);
  }, [twoFactorAuthenticationMobileNumber]);

  const openModalWindow = (type: string, mobileNumber?: { countryCode: string; number: string }) => {
    setModalWindow({ type, isOpen: true });
    if (mobileNumber) {
      setMobileNumber(mobileNumber);
    }
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
  };

  const handleStatusChange = (newStatus: TWO_FACTOR_AUTHENTICATION_STATUS_ENUM) => {
    setStatus(newStatus);
  };

  const handleVerificationId = (newVerificationId: string) => {
    setVerificationId(newVerificationId);
  };

  const handleSignOut = () => {
    signOut();
    navigate('/');
  };

  const handleTwoFactorAuthenticationErrors = (error: any, setErrorMessage: Dispatch<SetStateAction<string>>) => {
    switch (error?.code) {
      case 'auth/unverified-email':
        setErrorMessage('Need to verify email first before enrolling second factors.');
        break;
      case 'auth/second-factor-already-in-use':
        setErrorMessage('This phone number is already enrolled as a second factor. Please use a different phone number.');
        break;
      case 'auth/requires-recent-login':
      case 'auth/user-token-expired':
        signOut();
        navigate('/', {
          state: {
            twoFactorAuthenticationMobileNumber: mobileNumber,
            previousUserEmail: user?.email
          }
        });
        break;
      case 'auth/invalid-verification-code':
        handleStatusChange(TWO_FACTOR_AUTHENTICATION_STATUS_ENUM.EXPIRED);
        break;
      default:
        setErrorMessage(error?.message || defaultErrorMessage);
        break;
    }
  };

  const sendVerificationCode = async () => {
    const currentUser = auth?.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user found.');
    }

    const multiFactorSession = await multiFactor(currentUser).getSession();
    const phoneInfoOptions = {
      phoneNumber: `+${mobileNumber.countryCode}${mobileNumber.number}`,
      session: multiFactorSession
    };
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions);
    handleVerificationId(verificationId);
  };

  const resendVerificationCode = async () => {
    try {
      setErrorMessage(null);
      setResendVerificationCodeLoading(true);

      await sendVerificationCode();

      handleStatusChange(TWO_FACTOR_AUTHENTICATION_STATUS_ENUM.VERIFY);
    } catch (error: any) {
      console.error(error);
      console.log(error.code);
      handleTwoFactorAuthenticationErrors(error, setErrorMessage);
    } finally {
      setResendVerificationCodeLoading(false);
    }
  };

  const getContentByStatus = () => {
    switch (status) {
      case TWO_FACTOR_AUTHENTICATION_STATUS_ENUM.VERIFY:
        return (
          <VerifyMobilePhoneNumber
            mobileNumber={mobileNumber}
            verificationId={verificationId}
            sendVerificationCode={sendVerificationCode}
            handleStatusChange={handleStatusChange}
            handleTwoFactorAuthenticationErrors={handleTwoFactorAuthenticationErrors}
            changeMobileNumber={changeMobileNumber}
          />
        );
      case TWO_FACTOR_AUTHENTICATION_STATUS_ENUM.CONFIRMATION:
        return <ConfirmationStatus />;
      case TWO_FACTOR_AUTHENTICATION_STATUS_ENUM.EXPIRED:
        return (
          <>
            {resendVerificationCodeLoading && (
              <ModalWrappedContent
                isOpen={resendVerificationCodeLoading}
                title="Sending"
                showCancelButton={false}
                showCloseIcon={false}
                onClose={() => {}}
              >
                <ModalLoader />
              </ModalWrappedContent>
            )}
            <TwoFactorAuthentication>
              <TerminatedIcon width={90} height={90} />
              <TwoFactorAuthenticationTitle>Mobile Phone Number Not Verified</TwoFactorAuthenticationTitle>
              {errorMessage && (
                <Banner
                  icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                  title="Error"
                  description={errorMessage}
                  bgColor={themeVar('context.error')}
                />
              )}
              <TwoFactorAuthenticationDescription>
                The verification code you entered is either incorrect or has expired. You can try resending a new code to your mobile phone.
              </TwoFactorAuthenticationDescription>
            </TwoFactorAuthentication>
            <Button size="md" onClick={resendVerificationCode}>
              Resend Code
            </Button>
          </>
        );
      default:
        return <AddMobilePhoneNumber mobileNumber={mobileNumber} handleSignOut={handleSignOut} openModalWindow={openModalWindow} />;
    }
  };

  return (
    <>
      <Head>
        <title>Two-Factor Authentication</title>
      </Head>
      {modalWindow.isOpen && modalWindow.type === 'verification-required' && (
        <VerificationRequired
          isOpen={modalWindow.isOpen}
          mobileNumber={mobileNumber}
          sendVerificationCode={sendVerificationCode}
          handleStatusChange={handleStatusChange}
          handleTwoFactorAuthenticationErrors={handleTwoFactorAuthenticationErrors}
          onClose={closeModalWindow}
        />
      )}
      {getContentByStatus()}
      <RecaptchaContainer id="recaptcha-container" />
    </>
  );
};

export default TwoFactorAuthenticationPage;

export const TwoFactorAuthentication = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 10px;
  max-width: 420px;
  padding: 0 20px 30px 20px;
  width: 100%;
  border-bottom: 1px solid ${themeVar('border.base')};
`;

export const TwoFactorAuthenticationTitle = styled.h1`
  font-size: 33px;
  font-weight: 400;
  text-align: center;
  line-height: 46.2px;
  color: ${themeVar('font.strong')};
`;

export const TwoFactorAuthenticationDescription = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
  padding: 10px;
`;

const RecaptchaContainer = styled.div``;
